import Vue from "vue";
import App from "./App.vue";
import "./registerServiceWorker";
import router from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";
import { Auth0Plugin } from "./auth";
import _ from "lodash";
import httpClient from "./api/httpClient";
import dateFilter from "./helpers/filters/date";
import numeralFilter from "./helpers/filters/numeral";
import stringFilter from "./helpers/filters/string";
import coreScores from "@/helpers/enums/coreScores";
import images from "@/helpers/enums/images";
import enums from "@/helpers/enums";
import Avatar from "@/components/common/Avatar";
import UserAvatar from "@/components/common/UserAvatar";
import UserChip from "@/components/common/UserChip";
import TeamAvatar from "@/components/common/TeamAvatar";
import TeamChip from "@/components/common/TeamChip";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import MobileTooltip from "@/components/common/MobileTooltip";
import SingleListItem from "@/components/common/SingleListItem";
import InfoCard from "@/components/common/InfoCard";
import * as Sentry from "@sentry/browser";
import { Vue as VueIntegration } from "@sentry/integrations";
import { getColor, getRandomColors } from "@/helpers/colors";
import { logError } from "@/helpers/errors";
import rules from "@/helpers/rules";
import { VueMasonryPlugin } from "vue-masonry";
import { t, t2 } from "@/helpers/locales";
import helpscout from "@/helpers/enums/helpscout";

Vue.prototype._ = _;
Vue.prototype.$coreScores = coreScores;
Vue.prototype.$images = images;
Vue.prototype.$rules = rules;
Vue.prototype.$enums = enums;
Vue.prototype.$helpscout = helpscout;

Vue.filter("normalDate", dateFilter.normalDate);
Vue.filter("normalDateNoTz", dateFilter.normalDateNoTz);
Vue.filter("longDate", dateFilter.longDate);
Vue.filter("longDateWithTime", dateFilter.longDateWithTime);
Vue.filter("longDateNoTz", dateFilter.longDateNoTz);
Vue.filter("dateWithDowNoTz", dateFilter.dateWithDowNoTz);
Vue.filter("time", dateFilter.time);
Vue.filter("one_decimal", numeralFilter.one_decimal);
Vue.filter("no_decimal", numeralFilter.no_decimal);
Vue.filter("no_decimal_abs", numeralFilter.no_decimal_abs);
Vue.filter("lower", stringFilter.lower);
Vue.filter("capitalize", stringFilter.capitalize);
Vue.filter("ordinal_words", numeralFilter.ordinal_words);
Vue.filter("currency", numeralFilter.currency);
Vue.filter("currencyShort", numeralFilter.currencyShort);
Vue.filter("currencyBig", numeralFilter.currencyBig);
Vue.filter("year", dateFilter.year);
Vue.filter("monthDate", dateFilter.monthDate);
Vue.filter("monthDateNoTz", dateFilter.monthDateNoTz);
Vue.filter("monthYearNoTz", dateFilter.monthYearNoTz);

Vue.use(Auth0Plugin, {
  domain: process.env.VUE_APP_AUTH_DOMAIN,
  clientId: process.env.VUE_APP_AUTH_CLIENT_ID,
  audience: process.env.VUE_APP_AUTH_AUDIENCE,
  onRedirectCallback: appState => {
    let url = window.location.pathname;
    if (appState && appState.targetUrl) {
      url = appState.targetUrl;
    }
    if (url != window.location.pathname) {
      router.push(url);
    }
  }
});
Vue.use(VueMasonryPlugin);

Sentry.init({
  dsn: process.env.VUE_APP_SENTRY_URL,
  environment: process.env.NODE_ENV,
  integrations: [
    new VueIntegration({
      Vue,
      logErrors: true,
      attachProps: true
    })
  ],
  // We recommend adjusting this value in production, or using tracesSampler
  // for finer control
  tracesSampleRate: 1.0
});

Vue.component("avatar", Avatar);
Vue.component("user-avatar", UserAvatar);
Vue.component("user-chip", UserChip);
Vue.component("team-avatar", TeamAvatar);
Vue.component("team-chip", TeamChip);
Vue.component("loading-spinner", LoadingSpinner);
Vue.component("mobile-tooltip", MobileTooltip);
Vue.component("single-list-item", SingleListItem);
Vue.component("info-card", InfoCard);

Vue.prototype.$http = httpClient;

Vue.config.productionTip = false;

Vue.mixin({
  methods: {
    getColor,
    getRandomColors,
    logError,
    t,
    t2
  }
});

new Vue({
  router,
  store,
  vuetify,
  render: h => h(App),
  created() {
    const fn = () => {
      if (!authService.isAuthenticated) {
        return;
      }
      Promise.all([
        this.$store.dispatch("lookup/getLookups"),
        this.$store.dispatch("task/getTasks"),
        this.$store.dispatch("profile/getProfile")
      ])
        .then(() => {
          let profile = this.$store.getters["profile/profile"] || {};
          this.$vuetify.lang.current = profile.locale || "general";

          let beaconObj = {
            name: profile.fullName,
            email: profile.email,
            company: profile.companyName,
            jobTitle: profile.jobTitle,
            "phone-number": profile.phoneNumber
          };
          if (profile.avatarUrl) {
            beaconObj.avatar = profile.avatarUrl;
          }
          window.Beacon("identify", beaconObj);

          if (!profile.settings.isPaidPlan && this.$route.path == "/") {
            if (this.$auth.isSuperAdmin() || this.$auth.isCoreCoach()) {
              this.$router.push({ path: "/portal/customers" });
            }
          }
        })
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occurred while loading the application."
          );
        });
    };
    const authService = this.$auth;
    if (!authService.loading) {
      fn();
    }
    authService.$watch("loading", loading => {
      if (loading === false) {
        fn();
      }
    });
  }
}).$mount("#app");
